import { isEmpty, get } from 'lodash'
import { trackImpression, trackLinkWithData } from '../../lib/analytics/fpti'
import {
  getPaymentCardNetwork,
  getRelevantFundingSource,
} from 'transfer/utils/funding-option-utils'
import { trackClick3ds2 } from './unified-send-analytics'
import { startDDCCall, endDDCCall } from '../actions/send'

/**
 * Collect Browser Context only for 3DS2.0 Resolve Contingency
 * */
export function collectBrowserContext() {
  let browserColorDepth = `_${window.screen.colorDepth}BITS`
  const browserContext = {
    browser_screen_height: window.screen.height,
    browser_screen_width: window.screen.width,
    browser_color_depth: browserColorDepth,
    browser_timezone_offset_minutes: new Date().getTimezoneOffset(),
    browser_language: window.navigator.language,
    browser_java_enabled: !!window.navigator.javaEnabled(),
  }
  return browserContext
}

export function extract3DS2Contingency({ contingencies = [] }) {
  const foundContingency = contingencies.find(
    contingency => contingency.action === '3D_SECURE_DATA_COLLECTION_REQUIRED'
  )
  return foundContingency || {}
}

export function track3DS2PageImpression(
  pageGroup,
  pageName,
  fundingOption = {}
) {
  const { id: fundingOptionId } = fundingOption
  const threeDS2Contingency = extract3DS2Contingency(fundingOption)
  const has3DS2Contingency = !isEmpty(threeDS2Contingency)
  let threeDS2ReferenceId = ''

  if (has3DS2Contingency) {
    threeDS2ReferenceId = threeDS2Contingency.three_ds_reference_id
  }
  const fundingSource = getRelevantFundingSource(fundingOption)
  const network = getPaymentCardNetwork(fundingSource)

  const analyticsData = {
    fi_id: fundingOptionId,
    is_3ds_eligible: has3DS2Contingency,
    '3ds_ref_id': threeDS2ReferenceId,
    sel_fmx_tp: network,
  }
  trackImpression(pageGroup, pageName, analyticsData)
}

// Track impression for (3ds2.0) DDC start call after
// retrieving fundingOptions when the user clicks continue
export function track3ds2PageStartDDC(ddcInfo = {}, isGuest = false) {
  const {
    fundingOptionId = '',
    threeDS2ReferenceId = '',
    threeDSecureProtocol = '',
    deviceDataCollectionMode = '',
  } = ddcInfo
  const startTime = performance.now()
  const sharedAnalytics = {
    is_3ds_eligible: true,
    version: `3DS${threeDSecureProtocol}`,
    resp_duration: {
      fi_id: fundingOptionId,
      '3ds_ref_id': threeDS2ReferenceId,
      ddc_wait_time: deviceDataCollectionMode,
      ddc_start_time: startTime,
    },
  }
  if (isGuest) {
    return trackClick3ds2('guestSend', {
      fi_type: 'card',
      ...sharedAnalytics,
    })
  }
  const pageName = `main:p2p:choice:fioption::node`
  const pageGroup = `main:p2p:choice:fioption`
  const trackingData = { ...sharedAnalytics }
  trackLinkWithData('Continue', pageGroup, pageName, trackingData, 'link', {})
}

export function track3ds2PostDDCAnalytics(isGuest, startTime, endTime) {
  // Track the start and end times for various calls
  let analyticsPage = ''
  if (isGuest) {
    analyticsPage = 'guestReview'
  } else {
    analyticsPage = 'onClickSendPaymentNow'
  }
  trackClick3ds2(analyticsPage, {
    resp_duration: {
      lookup_start_time: startTime,
      Spinner_start_time: startTime,
      Spinner_end_time: performance.now(),
      lookup_end_time: endTime,
    },
  })
}

// Used for sync variant when the DDC call has completed
export function track3DS2EndDDC(ddcInfo = {}, wasSuccessful = true) {
  const pageGroup = 'main:p2p::cards:authenticate_with_card_network'
  const pageName = 'main:p2p::cards:authenticate_with_card_network::'
  const {
    fundingOptionId = '',
    threeDS2ReferenceId = '',
    threeDSecureProtocol = '',
    deviceDataCollectionMode = '',
  } = ddcInfo
  const endTime = performance.now()
  const trackingData = {
    is_3ds_eligible: true,
    version: `3DS${threeDSecureProtocol}`,
    resp_duration: {
      fi_id: fundingOptionId,
      '3ds_ref_id': threeDS2ReferenceId,
      ddc_wait_time: deviceDataCollectionMode,
      ddc_end_time: endTime,
      was_successful: wasSuccessful,
    },
  }
  trackLinkWithData(
    'Send Payment Now',
    pageGroup,
    pageName,
    trackingData,
    'link'
  )
}

/**
 * Resolves when DDC call event has completed
 * This function first dispatches the start DDC call and then returns a resolvable
 * promise that will wait until the cardinal collect event has fired.
 *
 * 1. Once fired: the listener is removed, end DDC is dispatched, and promise
 * is resolved.
 *
 * 2. If never fired (timeout) or errors: The promise will be resolved in order to
 * continue flow.
 *
 * @param {Function} dispatch - redux dispatch function
 * @param {Object} ddcInfo - contains DDC contigency info for funding option
 */
export function cardinalDDCCallPromise(dispatch, ddcInfo) {
  dispatch(startDDCCall(ddcInfo))
  const isGuest = window.location.href.includes('/guestLogin/payRequest/')
  track3ds2PageStartDDC(ddcInfo, isGuest)
  return new Promise(resolve => {
    let resolved = false
    // Listener function to handle success case
    const listener = function(event) {
      if (event.origin && event.origin.includes('cardinalcommerce.com')) {
        let data = null
        try {
          data = JSON.parse(event.data)
        } catch (exception) {
          // Error handler incase parsing goes wrong
          data = {}
        }
        if (get(data, 'MessageType', '') === 'profile.completed') {
          resolved = true
          window.removeEventListener('message', listener)
          track3DS2EndDDC(ddcInfo)
          dispatch(endDDCCall())
          resolve() // Resolve on success
        }
      }
    }

    // Start event listener to listen to successful response
    window.addEventListener('message', listener)

    // Resolve on timeout to get response after 15 seconds
    // Handles case where we get error or no response
    setTimeout(() => {
      if (!resolved) {
        window.removeEventListener('message', listener)
        track3DS2EndDDC(ddcInfo, false)
        dispatch(endDDCCall())
        resolve()
      }
    }, 15000)
  })
}

// Extracts deviceDataCollectionMode and check if it's the fire and forget variant
export function isFireAndForget(ddcCalls = []) {
  // We only need to check one call since if one is fire and forget, they shall all be.
  const ddcCall = ddcCalls[0]
  return get(ddcCall, 'deviceDataCollectionMode', '') === 'FIRE_AND_FORGET'
}

/**
 * This Function checks if  Browser back button is pressed while the 3DS process is ongoing.
 * This is required to handle duplicate lookup calls and avoid OverPanel Loop
 * https://github.paypal.com/ConsumerWeb-R/p2pnodeweb/pull/9514 -PR
 * https://engineering.paypalcorp.com/confluence/display/NGW/Duplicate+Look+up+call+Test+Cases
 * payRequest is ignore for this for two reasons, first if the user initiates a browser back then
 * the user is redirected to summary and secondly history.action is set to POP on load for pay request.
 */
export function isBrowserBackPressedFor3DSFlow(
  hasResolvedDDCContingencies,
  selectedFundingOption,
  hasResolved3DS2Contingencies,
  is3DS2ChallengeRequired,
  history
) {
  return (
    ((!hasResolvedDDCContingencies &&
      selectedFundingOption.hasDDCContingency) ||
      (!hasResolved3DS2Contingencies && is3DS2ChallengeRequired)) &&
    history &&
    history.action === 'POP' &&
    !history.location.pathname.includes('/payRequest')
  )
}
